import { mkNode, createErrorThumbnail, escapeHtml } from '@p4b/utils';
import { Thumbnail, ThumbnailBase } from '@p4b/thumbnail-base';
import { Img } from '@p4b/image-base';
import { rendererFromImage } from '@p4b/image-base';
import { isDicomImg } from '@p4b/Renderers/render-dicom';
import { ImageViewerElement } from '@p4b/image-viewer';
import { warnModal } from './modal-dialog';

const THUMBNAIL_HEIGHT = 120;

export class ResourceThumbnail extends ThumbnailBase implements Thumbnail {
    private thumb: HTMLCanvasElement;
    private cols: number;
    private rows: number;
    private imageData?: ImageData;

    public constructor(image: Img, frag: Node, releaseButton = false) {
        const {rows, cols} = isDicomImg(image) ? ((image.frames.length > 0) ? image.frames[0] : {"rows": THUMBNAIL_HEIGHT, "cols": THUMBNAIL_HEIGHT}) : image;
        const thumbCols = (cols != undefined && rows != undefined) ? Math.floor(THUMBNAIL_HEIGHT * cols / rows) : THUMBNAIL_HEIGHT;
        const thumbRows = THUMBNAIL_HEIGHT;
        let thumb: HTMLCanvasElement|null = null;
        super(frag, image, releaseButton, (figure: HTMLDivElement) => {
            thumb = mkNode('canvas', {
                className: 'thumbnail',
                parent: figure,
                style: {visibility: 'hidden', width: thumbCols + 'px', height: thumbRows + 'px'},
                attrib: {title: 'Click on the thumbnail to view and manipulate the image.', draggable: 'false'},
            });
            thumb.width = thumbCols;
            thumb.height = thumbRows;
        });
        this.cols = thumbCols;
        this.rows = thumbRows;
        if (!thumb) {
            throw "Thumbnail canvas is null.";
        }
        this.thumb = thumb;
        this.thumb.addEventListener('contextrestored', this.handleContextRestored);
    }

    public async load(thumbnail: ArrayBuffer|null): Promise<void> {
        try {
            const cxt = this.thumb.getContext('2d');
            if (cxt === null) {
                throw "Failed to get 2D context for thumbnail.";
            }
            if (!thumbnail) {
                this.cols = this.rows = this.thumb.width = this.thumb.height = THUMBNAIL_HEIGHT;
                this.thumb.style.width = this.thumb.style.height = THUMBNAIL_HEIGHT + 'px';
                this.imageData = createErrorThumbnail(THUMBNAIL_HEIGHT, THUMBNAIL_HEIGHT, 'Thumbnail', 'Thumbnail not available');
            } else if (thumbnail.byteLength !== this.cols * this.rows * 4) {
                this.cols = this.rows = this.thumb.width = this.thumb.height = THUMBNAIL_HEIGHT;
                this.thumb.style.width = this.thumb.style.height = THUMBNAIL_HEIGHT + 'px';
                this.imageData = createErrorThumbnail(THUMBNAIL_HEIGHT, THUMBNAIL_HEIGHT, 'Thumbnail', 'Buffer size mismatch');
            } else {
                const uint8clamped = new Uint8ClampedArray(thumbnail);
                this.imageData = new ImageData(uint8clamped, this.cols, this.rows);
            }
            cxt.putImageData(this.imageData, 0, 0);
            this.thumb.style.visibility = 'visible';
        } catch (error) {
            console.warn(`Failed to load thumbnail "${this.image.caption ?? this.image.id}". `, error);
            await warnModal({message: `<p>Failed to load thumbnail "${this.image.caption ?? this.image.id}".</p><p>${escapeHtml(String(error))}</p>`});
        }
    }

    private readonly handleContextRestored = () => {
        if (this.imageData) {
            const cxt = this.thumb.getContext('2d');
            if (cxt != null) {
                cxt.putImageData(this.imageData, 0, 0);
            }
        }
    }

    public open(viewer: ImageViewerElement) {
        viewer.setRenderer(rendererFromImage(this.image));
    }

    public async defferedLoad(viewer: ImageViewerElement): Promise<void> {
        await viewer.load();
    }

    public destroy(): void {
        super.destroy();
        this.thumb.removeEventListener('contextrestored', this.handleContextRestored);
        //console.debug('thumbnail: destroy');
    }
}
